import cx from 'classnames';
import styles from './styles.module.scss';
import { useEffect, useMemo, useRef, useState } from 'react';
import { USER_PROFILE_PLACEHOLDER } from '../../../constants/assetConstants';
import { icons } from '../../../assets';
import { DropdownMenu } from '../../subscriptionComponents/DropdownMenu';
import { emailIsValid, isTextureProductPage } from '../../../helpers/jsHelper';
import { MODE, useApiList } from '../../../hooks/useApiList';
import { v4 as uuidv4 } from 'uuid';
import { warningToast } from '../../../helpers/toastHelper';
import { ItemSelectListUI } from '../../../common/OrgSection/ItemSelectListUI';
const { WarningIcon, ArrowIconV2, TickIcon, Close } = icons;
import {
  PRODUCT_PAGE_SHARE_ACCESS,
  PRODUCT_PAGE_SHARE_ACCESS_LABEL,
  apiGetTheSharePage,
} from './index';
import { apiPostProductAccessPermission, saveProductPageShareData } from '../../../helpers/api';
import { useParams } from 'react-router-dom';
import { searchSelector } from '../../../redux/slicers/search/searchSlicer';
import { useDispatch, useSelector } from 'react-redux';
import { GetOrgMembersList, GetSearchMembersList } from '../../../api/share/share';
import { Loader } from '../../../common';
import { selectPublicLinkData } from '../../../redux/slicers/productV2Slicer';
import { selectTotalEditors, selectUsedEditors } from '../../../redux/slicers/subscriptionSlicer';
import { useUserContext } from '../../../contexts/UserContext';

// Debounce function
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Cleanup function to clear the timeout if value changes or component unmounts
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const InviteForm = ({ product, className = '', setSuccessModal = () => {} }) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { isGuestUser } = useUserContext();
  const { searchOrgResult } = useSelector(searchSelector);
  const publicLinkData = useSelector(selectPublicLinkData);
  const searchRef = useRef(null);
  const innerFormRef = useRef(null);

  const [submitting, setSubmitting] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [access, setAccess] = useState(PRODUCT_PAGE_SHARE_ACCESS.CAN_VIEW);

  const [selectedItems, setSelectedItems] = useState([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [showList, setShowList] = useState(false);
  const usedEditors = useSelector(selectUsedEditors);
  const totalEditors = useSelector(selectTotalEditors);
  const availableEditor = +totalEditors - +usedEditors

  const debouncedSearchText = useDebounce(searchText, 300); // 300 ms delay
  const orgList = searchOrgResult?.results || [];
  const { currentUser } = useUserContext();

  useEffect(async () => {
      if(searchText){
        dispatch(GetOrgMembersList(searchText));
      }
  }, [debouncedSearchText]);

  // Function to handle clicks outside the innerForm
  const handleClickOutside = (event) => {
    if (innerFormRef.current && !innerFormRef.current.contains(event.target)) setShowList(false); // Hide the list if clicked outside
  };

  useEffect(() => {
    // Add event listener for clicks
    document.addEventListener('mousedown', handleClickOutside);

    // Cleanup event listener on unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const params = useMemo(() => {
    return {
      params: {
        search: debouncedSearchText,
      },
    };
  }, [debouncedSearchText]);

  const dataFormatter = (item) => ({
    id: item?.id,
    title: item?.member_name,
    description: item?.member_email,
    image: item?.member_profile,
    userId: item?.user,
  });

  const { loading, hasMore, hasData, fetchNextPage, list } = useApiList({
    api: apiGetTheSharePage,
    mode: MODE.PARAMS,
    defaultPerPage: 5,
    params,
    dataFormatter,
  });

  const filteredList = useMemo(
    () => [...list, ...orgList]?.filter((a) => !selectedItems?.map((b) => b.id)?.includes(a.id)),
    [selectedItems, list, orgList]
  );

  const updateSelectedItem = (data = null) => {
    if (!data) {
      setIsSearchFocused(true);
      return;
    }
    setSelectedItems((prev) => [...prev, data]);

    setSearchText('');
    setIsSearchFocused(true);
    searchRef?.current?.focus();

    // Scroll to the bottom after adding the item
    setTimeout(() => searchRef?.current?.scrollIntoView({ behavior: 'smooth' }), 10);
  };

  const onInvite = async (selectedItems , access , list, isUpgradePopup) => {
    // TODO: integrate API to invite the user
      setSubmitting(true)
      const formData = new FormData()
      formData.append('permission_type',access)
      formData.append('unique_id',publicLinkData?.unique_code)
      formData.append('name',product?.name)
      formData.append(isTextureProductPage() ? "texture" : "product",product?.id || id)
      if(isGuestUser()){
        formData.append('is_guest_user',true)
      }
      if(selectedItems?.length){
        selectedItems?.map(list=> {
          if(list?.id && list?.name && list?.organization_owner_email && list?.owner){
            formData.append('shared_to_org',list?.id)
            const payload = {
              is_internal: false,
              user: currentUser?.user?.id,
              user_email: list?.organization_owner_email
            }
            handleSaveProductShareData(payload,product)
            return
          }
          if(list?.email && list?.title && list?.email === list?.title){
            formData.append('share_to_email',list?.email?.trim())
            const payload = {
              is_internal: false,
              user_email: list?.email?.trim()
            }
            handleSaveProductShareData(payload,product)
            return
          }
          if(list?.id && list?.description && list?.title){
            formData.append('shared_to_member',list?.id)
            const payload = {
              is_internal: true,
              user: list?.userId,
              user_email: list?.description
            }
            handleSaveProductShareData(payload,product)
            return
          }
        })
      }
      const res = await apiPostProductAccessPermission(formData)
      
      list !== "skip" && setSubmitting(false)
      if(list !== "skip" && (res && res?.status || res?.data?.data || res?.data)){
        setSuccessModal({list:list || selectedItems, isUpgradePopup:isUpgradePopup, accessType: access})
      }
  };

  const handleSaveProductShareData = (data,product) => {
    const payload = {
      ...data
    }
    if (isTextureProductPage()) {
      payload['texture'] = product?.id;
    } else {
      payload['product'] = product?.id;
    }
    saveProductPageShareData(payload);
  }

  const makeInviteAPI = () => {
    if(access === PRODUCT_PAGE_SHARE_ACCESS.CAN_VIEW || availableEditor === selectedItems?.length){
      onInvite(selectedItems , access, selectedItems)
    }else{
        const original = +availableEditor === 0 ? [] : selectedItems.slice(0,availableEditor)
        const remaining = selectedItems.slice(availableEditor)
        if(original?.length){
          onInvite(original, access, remaining?.length ? 'skip' : selectedItems)
        }
        if(remaining?.length){
          onInvite(remaining, PRODUCT_PAGE_SHARE_ACCESS.CAN_VIEW, selectedItems, true)
        }
    }
  }

  const onEnterPress = ({ key }) => {
    if (loading) return;

    if (key === 'Enter' && filteredList?.length > 0) {
      updateSelectedItem({
        ...filteredList?.[0],
        image: filteredList?.[0]?.image || USER_PROFILE_PLACEHOLDER,
      });
      return;
    }

    const email = searchText?.trim();

    if (key === 'Enter' && email) {
      const isExists = selectedItems.find((a) => a?.email?.toLowerCase() === email?.toLowerCase());

      if (isExists) {
        warningToast('Email already exists!');
        return;
      }

      if (!emailIsValid(email)) {
        warningToast('Invalid email!');
        return;
      }

      updateSelectedItem({
        id: uuidv4(),
        email: email,
        title: email,
        image: null, // USER_PROFILE_PLACEHOLDER,
        warning: !emailIsValid(email),
      });
    }
  };

  const onDelete = (id) => setSelectedItems((prev) => [...prev?.filter((a) => a.id !== id)]);

  const menuList = useMemo(
    () => [
      {
        id: 1,
        title: (
          <>
            <TickIcon style={{ opacity: access === PRODUCT_PAGE_SHARE_ACCESS.CAN_VIEW ? 1 : 0 }} />{' '}
            <span>{PRODUCT_PAGE_SHARE_ACCESS_LABEL[PRODUCT_PAGE_SHARE_ACCESS.CAN_VIEW]}</span>
          </>
        ),
        onClick: (v) => setAccess(PRODUCT_PAGE_SHARE_ACCESS.CAN_VIEW),
      },
      {
        id: 2,
        title: (
          <>
            <TickIcon style={{ opacity: access === PRODUCT_PAGE_SHARE_ACCESS.CAN_EDIT ? 1 : 0 }} />{' '}
            <span>{PRODUCT_PAGE_SHARE_ACCESS_LABEL[PRODUCT_PAGE_SHARE_ACCESS.CAN_EDIT]}</span>
          </>
        ),
        onClick: (v) => setAccess(PRODUCT_PAGE_SHARE_ACCESS.CAN_EDIT),
      },
    ],
    [access]
  );

  const AccessLabel = useMemo(
    () => (
      <div className={styles.menuLabel}>
        <span>{PRODUCT_PAGE_SHARE_ACCESS_LABEL[access]}</span>
        <ArrowIconV2 />
      </div>
    ),
    [access]
  );

  return (
    <>
      <div className={cx(styles.form, className)} ref={innerFormRef}>
        <div
          className={cx(styles.innerForm, isSearchFocused ? styles.focus : '')}
          onClick={(e) => {
            e?.stopPropagation();
            e?.preventDefault();
            if(!window.$insideDropdown){
              setShowList(true);
            }
            setIsSearchFocused(true);
            searchRef?.current?.focus();
          }}>
          <div className={cx(styles.selectedItems, 'main-search-part')}>
            {selectedItems?.map((item) => (
              <span
                key={item?.id}
                className={`search-selected-item ${item?.warning ? 'text-danger' : ''}`}>
                {item?.warning ? (
                  <WarningIcon />
                ) : item?.image ? (
                  <img className="search-user-icon" src={item?.image} />
                ) : null}
                <span className="search-selected-text">{item?.title || item?.name}</span>
                <span
                  onClick={() => onDelete(item?.id)}
                  className="round-close-btn"
                  style={{ cursor: 'pointer' }}>
                  <Close width={14} height={14} />
                </span>
              </span>
            ))}

            <input
              ref={searchRef}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={isSearchFocused ? '' : 'Enter teams or email addresses'}
              onKeyDown={onEnterPress}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              className='w-100'
            />
          </div>

          <DropdownMenu
            className={cx('m-0', styles.menu)}
            menu={menuList}
            button={AccessLabel}
            hideOption={showList}
            buttonProps={{
              onFocus: (e) => {
                e?.preventDefault();
                e?.stopPropagation();
                setShowList(false);
              },
              onMouseEnter: (e) => {
                window.$insideDropdown = true
                e?.preventDefault();
                e?.stopPropagation();
                setIsSearchFocused(false);
                setIsSearchFocused(false);
              },
              onMouseLeave: (e) => {
                e?.preventDefault();
                window.$insideDropdown = false
              },
            }}
          />

          {(searchText || showList) && filteredList.length > 0 ? (
            <ItemSelectListUI
              className={styles.list}
              items={filteredList}
              // selected={selectedUserId}
              onChange={(v) => {
                const exists = filteredList?.find((a) => a.id === v);
                if (!exists) return;
                updateSelectedItem({
                  ...exists,
                  image: exists?.image || USER_PROFILE_PLACEHOLDER,
                });
              }}
              loading={loading}
              hasData={hasData || filteredList?.length}
              fetchMoreData={fetchNextPage}
              hasMore={hasMore}
              placeholder={USER_PROFILE_PLACEHOLDER}
            />
          ) : null}
        </div>

        <button
          className={styles.btn}
          onClick={makeInviteAPI}
          disabled={
            submitting || selectedItems?.length === 0 || selectedItems?.some((a) => a?.warning)
          }>
          {submitting ? (
            <Loader
              loading={true}
              position="relative"
              borderColor="white"
              color="inherit"
              size={22}
              thickness={2}
            />
          ) : (
            <span>Invite</span>
          )}
        </button>
      </div>
    </>
  );
};
